/* Hash router + 3-area shell (shell restructure, spec A1/A5).
 *
 * ADR-009 step-1 discipline: this module is the WHOLE restructure. It does
 * NOT touch any view module. Every ported section still renders verbatim into
 * its existing `<div id="…Body">` mount (views/registry.ts's renderAll runs
 * unchanged, painting all nine at once); the router just decides which AREA is
 * visible for the current route and hides the rest. "Routes render the same
 * DOM the scroll did" — so the existing DOM/renderers are untouched.
 *
 * Areas + standing destinations, per spec A1 (client-first IA increment 1
 * added #/clients as the login landing — the router default swapped from
 * #/work to #/clients; every route below stays reachable and unchanged):
 *   #/clients         (DEFAULT)  → My Clients landing (client-first IA §1)
 *   #/work                       → the board / triage (§7)
 *   #/research/:section          → one research section per route (§1–§6)
 *   #/report                     → exec summary + appendix (§0, §8)
 *   #/portfolio                  → the multi-client home (cross-page today)
 *
 * The router is also the compatibility layer for the deep-link grammar it is
 * built ON (protect-list item 6, ws/taskLink.ts): legacy `#sN` anchors (incl.
 * buildTaskHref's `…#s7`), `#notes`, and bare `?task=` arrivals all resolve to
 * the right area here, so every external link and bookmark keeps working. It
 * deliberately does NOT rewrite the URL hash for legacy forms — leaving `#s7`
 * / `#notes` intact so ws/taskLink.ts's openTaskDeepLink still reads them.
 */

export type Area =
  | 'clients' | 'cockpit' | 'prospects' | 'team' | 'mywork' | 'work' | 'research' | 'report' | 'portfolio';
export type ResearchSub = 'search' | 'entity' | 'ai' | 'profiles' | 'wiki' | 'conflicts';
/** Client Cockpit tabs (client-first IA increment 2, spec §2; the anatomy §4.2
 * nav — Overview · Intelligence · Tasks · Requests · Activity · Deliverables —
 * maps onto these: `board` IS Tasks, the deep surfaces (wiki/search/news/ai/plan)
 * ARE Intelligence, and `requests`/`deliverables` are the two honest new tabs). */
export type CockpitTab =
  | 'overview' | 'board' | 'activity' | 'wiki' | 'search' | 'news' | 'ai' | 'plan'
  | 'requests' | 'deliverables';

export interface Route {
  area: Area;
  sub?: ResearchSub;
  /** the cockpit's client slug (area === 'cockpit'). */
  slug?: string;
  /** the cockpit's active tab (area === 'cockpit'); defaults to 'overview'. */
  tab?: CockpitTab;
  /** an aspect key focus (`#/client/{slug}/aspect/{key}`) — the cockpit scrolls
   * to that panel; overview tab. */
  aspect?: string;
}

/** The six research sub-sections, in nav order (01–06). */
export const RESEARCH_ORDER: readonly ResearchSub[] = [
  'search', 'entity', 'ai', 'profiles', 'wiki', 'conflicts',
];

/** research sub → the ported section's DOM id (verbatim from index.html). */
const SUB_TO_SECTION: Record<ResearchSub, string> = {
  search: 's1', entity: 's2', ai: 's3', profiles: 's4', wiki: 's5', conflicts: 's6',
};

/** Which chapter sections the transitional (deep-link-only) areas show. Report
 * carries the exec summary AND the appendix (both client-facing reading
 * surfaces). Research/report/work have no rail nav entry any more — they stay
 * reachable by legacy deep link (#sN, #/research/…, #/report) until later
 * packages port those workspaces into the CRM shell. */
const AREA_SECTIONS: Record<'work' | 'research' | 'report', string[]> = {
  work: ['s7'],
  research: ['s1', 's2', 's3', 's4', 's5', 's6'],
  report: ['s0', 's8'],
};

/** The CRM register (client book) landing surface — toggled by the same
 * show-one-area/hide-the-rest mechanism as every routed section. */
const CLIENTS_SECTION = 'clientsView';
/** The Client Cockpit surface (transitional client file) — toggled the same. */
const COCKPIT_SECTION = 'cockpitView';
const PROSPECTS_SECTION = 'prospectsView';
const TEAM_SECTION = 'teamView';
/** The CRM My Work inbox (#/work, Package 5) — the cross-client work view. It is
 * a SEPARATE area from the transitional legacy board (`work`, reachable by the
 * `#s7` deep link + the cockpit Board tab), so both keep working. */
const WORK_SECTION = 'workView';
const ALL_SECTIONS = [
  CLIENTS_SECTION, COCKPIT_SECTION, PROSPECTS_SECTION, TEAM_SECTION, WORK_SECTION,
  's0', 's1', 's2', 's3', 's4', 's5', 's6', 's7', 's8',
];

/** Legacy `#sN` anchor → route. Keeps buildTaskHref's `#s7`, bookmarks, and
 * shared section links (`#s0` … `#s8`) resolving after the restructure. */
const LEGACY_SECTION: Record<string, Route> = {
  s0: { area: 'report' },
  s1: { area: 'research', sub: 'search' },
  s2: { area: 'research', sub: 'entity' },
  s3: { area: 'research', sub: 'ai' },
  s4: { area: 'research', sub: 'profiles' },
  s5: { area: 'research', sub: 'wiki' },
  s6: { area: 'research', sub: 'conflicts' },
  s7: { area: 'work' },
  s8: { area: 'report' },
};

/** The login landing swaps from #/work to #/clients (client-first IA, spec §7
 * step 1). A bare/garbage hash therefore lands on My Clients — #/work and every
 * legacy route stay reachable and unchanged. */
export const DEFAULT_ROUTE: Route = { area: 'clients' };

/** Pure: hash string → Route. Total — anything unrecognized falls back to the
 * default (Work), so a stray/garbage hash never blanks the shell. Handles the
 * new `#/…` forms, the legacy `#sN` anchors, and `#notes` (client-note mention
 * landing — the notes card lives in the Report area). */
export function parseRoute(hash: string): Route {
  const h = hash.replace(/^#/, '');
  if (!h) return DEFAULT_ROUTE;

  if (h.startsWith('/')) {
    // strip any query fragment carried inside the hash (e.g. #/work?cat=content
    // — the writer persona landing; the `cat` filter is wired in a later chunk)
    const path = h.slice(1).split('?')[0] ?? '';
    const parts = path.split('/');
    const [head = '', tail = ''] = parts;
    switch (head) {
      case 'clients': return { area: 'clients' };
      case 'prospects': return { area: 'prospects' };
      case 'team': return { area: 'team' };
      case 'client': {
        // #/client/{slug} · #/client/{slug}/board · #/client/{slug}/activity ·
        // #/client/{slug}/aspect/{key}. A bare/garbage tail falls to Overview;
        // a slug is mandatory (no slug → the My Clients landing).
        const slug = tail;
        if (!slug) return DEFAULT_ROUTE;
        const seg = parts[2] || '';
        if (seg === 'board') return { area: 'cockpit', slug, tab: 'board' };
        if (seg === 'activity') return { area: 'cockpit', slug, tab: 'activity' };
        if (seg === 'wiki') return { area: 'cockpit', slug, tab: 'wiki' };
        if (seg === 'search') return { area: 'cockpit', slug, tab: 'search' };
        if (seg === 'news') return { area: 'cockpit', slug, tab: 'news' };
        if (seg === 'ai') return { area: 'cockpit', slug, tab: 'ai' };
        if (seg === 'plan') return { area: 'cockpit', slug, tab: 'plan' };
        if (seg === 'requests') return { area: 'cockpit', slug, tab: 'requests' };
        if (seg === 'deliverables') return { area: 'cockpit', slug, tab: 'deliverables' };
        if (seg === 'aspect') return { area: 'cockpit', slug, tab: 'overview', aspect: parts[3] || '' };
        return { area: 'cockpit', slug, tab: 'overview' };
      }
      // The CRM rail's My Work (rail item 02) → the cross-client inbox
      // (Package 5). The legacy per-client board keeps its own `work` area,
      // reachable by the `#s7` deep link (buildTaskHref) + the cockpit Board tab.
      case 'work': return { area: 'mywork' };
      case 'report': return { area: 'report' };
      case 'portfolio': return { area: 'portfolio' };
      case 'research': {
        const sub = (RESEARCH_ORDER as readonly string[]).includes(tail)
          ? (tail as ResearchSub) : 'search';
        return { area: 'research', sub };
      }
      default: return DEFAULT_ROUTE;
    }
  }

  // #notes → the notes card is in the Report area (§0); openTaskDeepLink
  // focuses it once the hash survives here (we don't rewrite it).
  if (h === 'notes') return { area: 'report' };

  return LEGACY_SECTION[h] ?? DEFAULT_ROUTE;
}

/** The route for the URL as it stands right now. */
export function currentRoute(): Route {
  return parseRoute(location.hash);
}

/** The section ids visible for a route (Portfolio has none — it redirects to
 * the client register). */
export function visibleSections(route: Route): string[] {
  if (route.area === 'portfolio') return [];
  if (route.area === 'clients') return [CLIENTS_SECTION];
  if (route.area === 'cockpit') return [COCKPIT_SECTION];
  if (route.area === 'prospects') return [PROSPECTS_SECTION];
  if (route.area === 'team') return [TEAM_SECTION];
  if (route.area === 'mywork') return [WORK_SECTION];
  if (route.area === 'research') {
    return [SUB_TO_SECTION[route.sub ?? 'search']];
  }
  return AREA_SECTIONS[route.area];
}

/** Apply a route to the DOM: show its section(s), hide the rest, set the area
 * class (drives the width system in app.css) and the active nav state. Returns
 * false if it redirected off-page (Portfolio) so the caller can bail. */
export function applyRoute(route: Route = currentRoute()): boolean {
  if (route.area === 'portfolio') {
    // Portfolio's rail entry is gone (Package 1). The old `#/portfolio` route
    // (bookmarks, a persona landing that predates the CRM shell) now redirects
    // to the client register — replace(), NOT assign(), so it isn't a
    // back-button trap. portfolio.html stays on disk but nothing links to it.
    location.replace('#/clients');
    return false;
  }

  const visible = new Set(visibleSections(route));
  for (const id of ALL_SECTIONS) {
    const el = document.getElementById(id);
    if (el) el.classList.toggle('hidden', !visible.has(id));
  }

  const doc = document.getElementById('doc');
  if (doc) {
    doc.classList.remove(
      'area-clients', 'area-cockpit', 'area-prospects', 'area-team',
      'area-mywork', 'area-work', 'area-research', 'area-report',
    );
    doc.classList.add(`area-${route.area}`);
  }

  // Rail nav active state. The cockpit is a child of the client register
  // (there's no cockpit rail item), so it lights the Clients ('clients') entry;
  // the My Work inbox (`mywork`) lights the same rail item as the legacy board
  // (`data-route="work"`), so the CRM rail item stays active on #/work.
  const navArea = route.area === 'cockpit' ? 'clients'
    : route.area === 'mywork' ? 'work' : route.area;
  document.querySelectorAll<HTMLElement>('.rail-link[data-route]').forEach((a) => {
    const on = a.dataset.route === navArea;
    a.classList.toggle('active', on);
    if (on) a.setAttribute('aria-current', 'page');
    else a.removeAttribute('aria-current');
  });

  updateCrumb(route);

  // A shell-level class the transitional cockpit CSS still keys off (kept for
  // the deep-link cockpit render until Package 2 replaces that surface).
  document.getElementById('shell')?.classList.toggle('cockpit-mode', route.area === 'cockpit');

  return true;
}

/** slug → brand, learned from the shared GET /clients roster (main.ts feeds it
 * via setClientBrands). Lets the cockpit breadcrumb read "Clients / El Al"
 * instead of the raw slug. */
const slugBrand = new Map<string, string>();

/** Feed the slug→brand map from the live roster. If we're currently sitting on
 * a cockpit whose brand just became known, re-render the crumb so it upgrades
 * from the slug to the brand in place. */
export function setClientBrands(rows: readonly { slug: string; brand: string }[]): void {
  let changed = false;
  for (const r of rows) {
    if (r.slug && r.brand && slugBrand.get(r.slug) !== r.brand) {
      slugBrand.set(r.slug, r.brand);
      changed = true;
    }
  }
  if (changed && currentRoute().area === 'cockpit') updateCrumb(currentRoute());
}

/** The topbar breadcrumb for a route (`#crumb`). Built with DOM nodes (not
 * innerHTML) so a slug can never inject markup. Primary segment + optional
 * trailing detail, matching the design's `<span>…</span><b>/</b><strong>…` grammar. */
function updateCrumb(route: Route): void {
  const el = document.getElementById('crumb');
  if (!el) return;
  let primary = 'Client book';
  let detail: string | null = null;
  switch (route.area) {
    case 'clients': primary = 'Client book'; detail = 'All clients'; break;
    case 'cockpit': primary = 'Clients'; detail = (route.slug ? slugBrand.get(route.slug) : null) ?? route.slug ?? null; break;
    case 'prospects': primary = 'Prospects'; break;
    case 'team': primary = 'Team'; detail = 'Account management'; break;
    case 'mywork': primary = 'My Work'; detail = 'Across your book'; break;
    case 'work': primary = 'My Work'; break;
    case 'research': primary = 'Client intelligence'; detail = route.sub ?? null; break;
    case 'report': primary = 'Report'; break;
    default: primary = 'Client book';
  }
  el.replaceChildren();
  const span = document.createElement('span');
  span.textContent = primary;
  el.appendChild(span);
  if (detail) {
    const sep = document.createElement('b');
    sep.textContent = '/';
    const strong = document.createElement('strong');
    strong.textContent = detail;
    el.append(sep, strong);
  }
}

/** Wire the router: re-apply on every hashchange (and scroll to top on a real
 * navigation — a deep-linked `?task=`/row reveal does its own scrolling and
 * doesn't change the hash, so it never fights this). Idempotent to call once.
 *
 * `onNavigate` lets main.ts run per-route side effects (none needed in step 1).
 */
export function initRouter(onNavigate?: (route: Route) => void): void {
  window.addEventListener('hashchange', () => {
    const route = currentRoute();
    if (applyRoute(route)) {
      window.scrollTo(0, 0);
      onNavigate?.(route);
    }
  });
}
